import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { HeatmapLegend } from '@/components/dashboard-widgets';
import { DemandStationsLayer, RequestDotsLayer } from '@/components/map/demand-stations-layer';
import { HeatmapLayers } from '@/components/map/heatmap-layers';
import { MapView } from '@/components/map/map-view';
import { StationsLayer } from '@/components/map/stations-layer';
import { Select } from '@/components/ui/form';
import { Card, ColorDot, PageHeader, SearchInput, Spinner } from '@/components/ui/layout';
import { locale, t } from '@/lib/i18n';
import { useNetwork } from '@/lib/network';
import { type DayPart, useDemand, useHeatmap, useStationDemand } from '@/lib/stats';

const WEEKDAYS = [t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat'), t('Sun')];
const n = (x: number) => x.toLocaleString(locale);

/** Change against the period before, e.g. "+12%" (green) or "−5%" (red). */
function Change({ now, before }: { now: number; before: number }) {
  if (!before) return <span className="text-slate-400">{now ? t('new') : '—'}</span>;
  const pct = Math.round(((now - before) / before) * 100);
  return (
    <span className={pct > 0 ? 'text-emerald-700' : pct < 0 ? 'text-red-700' : 'text-slate-500'}>
      {pct > 0 ? '+' : pct < 0 ? '−' : ''}
      {Math.abs(pct)}%
    </span>
  );
}

/** Simple bar chart; the highest bar is highlighted. */
function Bars({ values, labels, every = 1 }: { values: number[]; labels: string[]; every?: number }) {
  const max = Math.max(1, ...values);
  const top = values.indexOf(Math.max(...values));
  return (
    <div>
      <div className="flex h-28 items-end gap-[3px] border-b border-slate-200">
        {values.map((v, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed positions (hours, days)
            key={i}
            className={`flex-1 rounded-t-sm ${i === top && v > 0 ? 'bg-brand-600' : 'bg-brand-600/40'}`}
            style={{ height: `${(v / max) * 100}%` }}
            title={`${labels[i]}: ${n(v)}`}
          />
        ))}
      </div>
      <div className="mt-1 flex gap-[3px] text-[10px] text-slate-500">
        {labels.map((l, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed positions (hours, days)
          <div key={i} className="flex-1 text-center">
            {i % every === 0 ? l : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: React.ReactNode }) {
  return (
    <Card>
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </Card>
  );
}

/**
 * Passenger demand: how often and where people ask the app for arrivals (a station opened, a bus
 * tapped), by hour and weekday, per station and line, and on a heatmap.
 */
export function DemandPage() {
  const [days, setDays] = useState(7);
  const [part, setPart] = useState<DayPart>('all');
  const [q, setQ] = useState('');
  /** The station whose requests the heatmap shows (null: all passengers). */
  const [focus, setFocus] = useState<string | null>(null);
  /** How the request locations are drawn: blurred heatmap, dots, or not at all. */
  const [view, setView] = useState<'heat' | 'dots' | 'off'>('heat');
  const [showStations, setShowStations] = useState(true);
  const demand = useDemand(days);
  const heatmap = useHeatmap('user', days, part);
  const stationDemand = useStationDemand(focus, days);
  const network = useNetwork();
  const d = demand.data;

  const coords = useMemo(
    () => new Map((network.data?.stations ?? []).map((s) => [s._id, s.geometry?.coordinates])),
    [network.data],
  );
  const bubbles = useMemo(
    () =>
      (d?.stations ?? []).flatMap((s) => {
        const c = coords.get(s._id);
        return c ? [{ id: s._id, name: s.name, lng: c[0]!, lat: c[1]!, n: s.n }] : [];
      }),
    [d, coords],
  );
  const focused = d?.stations.find((s) => s._id === focus);
  const cells = focus ? (stationDemand.data?.cells ?? []) : (heatmap.data?.cells ?? []);

  const stations = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (d?.stations ?? []).filter((x) => !s || x.name.toLowerCase().includes(s));
  }, [d, q]);

  const peakHour = d ? d.by_hour.indexOf(Math.max(...d.by_hour)) : -1;
  const peakDay = d ? d.by_weekday.indexOf(Math.max(...d.by_weekday)) : -1;

  return (
    <>
      <PageHeader
        title={t('Passenger demand')}
        subtitle={t(
          'How often and where people ask the app when the bus comes: a station opened or a bus tapped, with the phone’s location.',
        )}
        actions={
          <Select
            className="w-40"
            aria-label={t('Period')}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={1}>{t('Today')}</option>
            <option value={7}>{t('Last {n} days', { n: 7 })}</option>
            <option value={30}>{t('Last {n} days', { n: 30 })}</option>
            <option value={90}>{t('Last {n} days', { n: 90 })}</option>
          </Select>
        }
      />
      {!d ? (
        <Spinner />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label={t('Requests')}
              value={n(d.total)}
              sub={
                <>
                  <Change now={d.total} before={d.previous_total} />{' '}
                  {days === 1 ? t('vs yesterday') : t('vs the {n} days before', { n: days })}
                </>
              }
            />
            <Stat label={t('Per day')} value={n(Math.round(d.total / days))} />
            <Stat
              label={t('Busiest hour')}
              value={
                d.total
                  ? `${String(peakHour).padStart(2, '0')}:00–${String(peakHour + 1).padStart(2, '0')}:00`
                  : '—'
              }
            />
            <Stat label={t('Busiest day')} value={d.total && days >= 7 ? (WEEKDAYS[peakDay] ?? '—') : '—'} />
          </div>

          <Card
            padded={false}
            title={t('Where people use the app')}
            actions={
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Select
                  className="w-40"
                  aria-label={t('Request locations')}
                  value={view}
                  onChange={(e) => setView(e.target.value as 'heat' | 'dots' | 'off')}
                >
                  <option value="heat">{t('Heatmap')}</option>
                  <option value="dots">{t('Dots (about 100 m)')}</option>
                  <option value="off">{t('Hidden')}</option>
                </Select>
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={showStations}
                    onChange={(e) => setShowStations(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  {t('Requested stations')}
                </label>
                <Select
                  className="w-44"
                  aria-label={t('Time of day')}
                  value={part}
                  disabled={!!focus}
                  onChange={(e) => setPart(e.target.value as DayPart)}
                >
                  <option value="all">{t('All day')}</option>
                  <option value="morning">{t('Morning (6–10)')}</option>
                  <option value="midday">{t('Midday (10–15)')}</option>
                  <option value="afternoon">{t('Afternoon (15–19)')}</option>
                  <option value="evening">{t('Evening (19–24)')}</option>
                  <option value="night">{t('Night (0–6)')}</option>
                </Select>
              </div>
            }
          >
            <div className="relative">
              <MapView className="h-[560px] rounded-none border-0">
                <HeatmapLayers kind={view === 'heat' ? 'user' : null} cells={cells} />
                {view === 'dots' && <RequestDotsLayer cells={cells} />}
                <StationsLayer
                  dim={0.35}
                  stations={network.data?.stations ?? []}
                  selected={focus}
                  onSelect={setFocus}
                />
                {showStations && <DemandStationsLayer stations={bubbles} focus={focus} onSelect={setFocus} />}
              </MapView>
              <div className="absolute right-2 bottom-2 z-10 rounded-md bg-white/95 px-2.5 py-2 text-[11px] text-slate-600 shadow ring-1 ring-slate-200">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block size-2.5 rounded-full bg-indigo-600" />
                  {t('Where the phone was when asking')}
                </div>
                {showStations && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className="inline-block size-2.5 rounded-full border-2 border-amber-500" />
                    {t('Station asked about (bigger: more requests)')}
                  </div>
                )}
                <div className="mt-1 text-slate-400">{t('Phones without a location are left out.')}</div>
              </div>
              {view === 'heat' && <HeatmapLegend kind="user" />}
              {focus && (
                <div className="absolute top-2 left-2 z-10 w-72 rounded-md bg-white/95 p-3 text-sm shadow ring-1 ring-slate-200">
                  <div className="font-semibold text-slate-900">
                    {focused?.name ?? stationDemand.data?.station?.name ?? '…'}
                  </div>
                  <div className="mt-1 text-slate-600">
                    {t('{n} requests in this period', { n: n(stationDemand.data?.total ?? focused?.n ?? 0) })}
                  </div>
                  {stationDemand.data?.median_m != null && (
                    <div className="text-slate-600">
                      {t('Half of them from within {m} m', { m: n(stationDemand.data.median_m) })}
                    </div>
                  )}
                  <p className="mt-1.5 text-xs text-slate-500">
                    {t('The heatmap shows where the people asking about this station were.')}
                  </p>
                  <div className="mt-2 flex gap-3">
                    <Link to={`/stations/${focus}`} className="font-medium text-brand-700 hover:underline">
                      {t('Open station')}
                    </Link>
                    <button
                      type="button"
                      className="font-medium text-slate-600 hover:underline"
                      onClick={() => setFocus(null)}
                    >
                      {t('All passengers')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card title={t('By hour of day')}>
              <Bars values={d.by_hour} labels={d.by_hour.map((_, h) => String(h))} every={3} />
            </Card>
            <Card title={days >= 7 ? t('By day of week') : t('By day')}>
              {days >= 7 ? (
                <Bars values={d.by_weekday} labels={WEEKDAYS} />
              ) : (
                <Bars values={d.by_day.map((x) => x.n)} labels={d.by_day.map((x) => x.d.slice(5))} />
              )}
            </Card>
          </div>
          {days > 7 && (
            <Card title={t('By day')}>
              <Bars
                values={d.by_day.map((x) => x.n)}
                labels={d.by_day.map((x) => x.d.slice(8))}
                every={days > 30 ? 7 : 2}
              />
            </Card>
          )}

          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <Card
              padded={false}
              title={t('Stations')}
              actions={<SearchInput value={q} onChange={setQ} placeholder={t('Search stations…')} />}
            >
              {stations.length === 0 ? (
                <p className="px-4 py-4 text-sm text-slate-500">{t('No requests in this period.')}</p>
              ) : (
                <div className="max-h-[560px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                      <tr>
                        <th className="px-4 py-2 font-medium">{t('Station')}</th>
                        <th className="px-4 py-2 text-right font-medium">{t('Requests')}</th>
                        <th className="px-4 py-2 text-right font-medium">{t('Change')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stations.map((s) => (
                        <tr
                          key={s._id}
                          className={`cursor-pointer border-t border-slate-100 ${focus === s._id ? 'bg-amber-50' : 'hover:bg-slate-50'}`}
                          onClick={() => setFocus(s._id)}
                        >
                          <td className="px-4 py-1.5">
                            <span className="text-slate-900">{s.name}</span>
                          </td>
                          <td className="px-4 py-1.5 text-right tabular-nums">{n(s.n)}</td>
                          <td className="px-4 py-1.5 text-right tabular-nums">
                            <Change now={s.n} before={s.prev} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            <Card padded={false} title={t('Lines (buses tapped)')}>
              {d.lines.length === 0 ? (
                <p className="px-4 py-4 text-sm text-slate-500">{t('No requests in this period.')}</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {d.lines.map((l) => (
                      <tr key={l._id ?? 'none'} className="border-t border-slate-100 first:border-t-0">
                        <td className="px-4 py-1.5">
                          <span className="flex items-center gap-2">
                            <ColorDot color={l.color} />
                            {l._id ? (
                              <Link to={`/lines/${l._id}`} className="text-slate-900 hover:underline">
                                {l.name}
                              </Link>
                            ) : (
                              <span className="text-slate-500">{t('No line')}</span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-1.5 text-right tabular-nums">{n(l.n)}</td>
                        <td className="px-4 py-1.5 text-right tabular-nums">
                          <Change now={l.n} before={l.prev} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
                {t('A tapped bus counts for the line it is on now.')}
              </p>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
